import './TextField.css'

import { ChangeEvent, FocusEvent, useRef, useState } from 'react'

import { useFocus } from '../hooks/useFocus'
import { cx } from '../utilities/cx'
import { fieldHoc } from './FieldHoc'
import { Icon, IconName } from './Icon'
import { Label } from './Label'

export type TextFieldKind = 'text' | 'password' | 'search'

export type TextFieldProps = {
  className?: string
  label?: string
  icon?: IconName
  leftIcon?: IconName
  rightIcon?: IconName
  kind?: TextFieldKind
  clearable?: boolean
  placeholder?: string
  onFocus?: () => void
  onBlur?: () => void
}

export const TextField = fieldHoc<string, TextFieldProps>((props) => {
  const {
    className,
    label,
    icon,
    leftIcon = icon,
    rightIcon,
    kind,
    size,
    clearable,
    placeholder,
    value = '',
    disabled,
    hasError,
    onChange,
    onFocus,
    onBlur,
  } = props

  const focus = useFocus()
  const frameRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isActive, setIsFocused] = useState(false)

  const handleFrameFocus = () => {
    if (isActive) return
    setIsFocused(true)
    onFocus?.()
  }

  const handleFrameBlur = (event: FocusEvent) => {
    if (frameRef.current?.contains(event.relatedTarget as Node)) return
    setIsFocused(false)
    onBlur?.()
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  const handleClear = () => {
    onChange('')
    inputRef.current?.focus()
  }

  const frameClasses = cx('text-field__frame', {
    'text-field__frame--active': isActive,
    'text-field__frame--disabled': disabled,
    'text-field__frame--error': hasError,
  })

  const inputClasses = cx('text-field__input', { 'text-field__input--error': hasError })

  return (
    <div className={cx('text-field', { 'text-field--small': size === 'small' }, className)}>
      {label && <Label>{label}</Label>}
      <div
        className={frameClasses}
        ref={focus.ref({ ref: frameRef, focusTarget: inputRef })}
        onFocus={handleFrameFocus}
        onBlur={handleFrameBlur}
      >
        {kind === 'search' && (
          <div className="text-field__icon text-field__icon--left">
            <Icon name="search" />
          </div>
        )}
        {!!leftIcon && (
          <div className="text-field__icon text-field__icon--left">
            <Icon name={leftIcon} />
          </div>
        )}
        <input
          className={inputClasses}
          type={kind === 'password' ? 'password' : 'text'}
          placeholder={placeholder}
          aria-label={label ?? placeholder}
          value={value}
          disabled={disabled}
          ref={inputRef}
          onChange={handleChange}
        />
        {!!clearable && !!value && (
          <div
            className="text-field__clear"
            ref={focus.ref({ onSelect: handleClear })}
            onClick={handleClear}
          >
            <div className="text-field__icon text-field__icon--right">
              <Icon name="circle-x" />
            </div>
          </div>
        )}
        {!!rightIcon && (
          <div className="text-field__icon text-field__icon--right">
            <Icon name={rightIcon} />
          </div>
        )}
      </div>
    </div>
  )
})
